import { UploadService } from "@/services/APIs";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const CustomFileUpload = (
  props: UploadProps & {
    action: string;
    value?: File[] | string[];
    onChange?: (value: File[] | string[]) => void;
    defaultFileList?: File[];
    /**
     * When set, POST the file as multipart field `file` to this URL (same as curl -F file=@...).
     * Skips UploadService. Use onParseResult to read JSON (e.g. auto-fill form fields).
     */
    parseResumeUrl?: string;
    onParseResult?: (data: unknown) => void;
    onParseError?: (message: string) => void;
  }
) => {
  const { id } = useParams();
  const editMode = !!id;
  const {
    value = [],
    onChange,
    parseResumeUrl,
    onParseResult,
    onParseError,
    ...restProps
  } = props;
  // const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
  // const editMode = !!id;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any>();

  useEffect(() => {
    if (!editMode && !value) {
      return;
    }

    try {
      let parsedValue = value;

      if (typeof value === "string") {
        parsedValue = JSON.parse(value);
      }

      if (!Array.isArray(parsedValue)) {
        parsedValue = [parsedValue];
      }

      // 🛑 Only update if the value actually changed
      if (JSON.stringify(parsedValue) !== JSON.stringify(uploadedFiles)) {
        setUploadedFiles(parsedValue);
      }
    } catch (e) {
      console.error("Error parsing uploaded files:", e);
      if (uploadedFiles !== null && uploadedFiles !== undefined) {
        setUploadedFiles([]);
      }
    }
  }, [editMode, value, uploadedFiles]); // 👈 include uploadedFiles in deps

  const handleFileSelect = async (file: File) => {
    if (parseResumeUrl) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(parseResumeUrl, {
          method: "POST",
          body: formData,
        });

        const text = await res.text();
        let data: unknown = text;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          /* non-JSON */
        }

        if (!res.ok) {
          const msg =
            typeof data === "object" && data !== null && "error" in data
              ? String((data as { error: unknown }).error)
              : text || res.statusText;
          onParseError?.(msg);
          console.error("parse-resume failed:", msg);
          return false;
        }

        onParseResult?.(data);
        setSelectedFiles([file]);
        setUploadedFiles([
          {
            file_original_name: file.name,
            file_path: "",
          },
        ]);
      } catch (error) {
        const msg =
          error instanceof Error ? error.message : "Resume parse request failed";
        onParseError?.(msg);
        console.error("parse-resume error:", error);
      }
      return false;
    }

    const newFiles = [...selectedFiles, file];
    setSelectedFiles(newFiles);
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: newFiles,
      });

      if (response && Array.isArray(response)) {
        const uploadedUrls = response;
        const safeValue = Array.isArray(value) ? value : [];
        const newValue = [
          ...safeValue.filter(item => typeof item === "string"),
          ...uploadedUrls,
        ];

        onChange?.(newValue);
        setUploadedFiles(newValue);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileToRemove = file.file_path ?? file.url ?? file.name;
    const filteredFiles = uploadedFiles.filter(
      (f: any) => (f.file_path ?? f.url ?? f.name) !== fileToRemove
    );

    setUploadedFiles(filteredFiles);
    setSelectedFiles([]);
    onChange?.(filteredFiles);
  };

  const handleChange = (info: any) => {
    if (info.file.status === "removed") {
      handleRemove(info.file);
    }
  };

  const handlePreview = (file: any) => {
    let url = file.url ?? file.file_path;

    if (!url) {
      return;
    }

    // 🔒 Force HTTPS
    if (url.startsWith("http://djangoproject.universal-software.in")) {
      url = url.replace("http://", "https://");
    }

    window.open(url, "_blank");
  };

  const fileList: UploadFile[] = uploadedFiles?.map(
    (file: any, index: number) => {
      if (
        typeof file === "object" &&
        (file?.file_path || file?.file_original_name)
      ) {
        return {
          uid: file?.id ?? `rc-upload-${index}`,
          name: file?.file_original_name ?? file?.name ?? "file",
          status: "done",
          url: file?.file_path ?? file?.url,
        };
      }

      return {
        uid: `rc-upload-${index}`,
        name: "No file",
        status: "done",
        url: "",
      };
    }
  );

  const showUploadButton =
    !uploadedFiles ||
    uploadedFiles.length === 0 ||
    uploadedFiles.every((f: any) => f === null);

  return (
    <div className="space-y-2">
      <Upload
        {...restProps}
        beforeUpload={handleFileSelect}
        onChange={handleChange}
        onRemove={handleRemove}
        maxCount={1}
        fileList={fileList}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        onPreview={handlePreview} // 👈 handle click event on file name or icon
      >
        {showUploadButton && (
          <Button icon={<UploadOutlined />}>Upload File</Button>
        )}
      </Upload>
    </div>
  );
};
